import "./stripeConnectedAccountSettings.scss";
import { Alert, Result, Skeleton, Space, Typography } from "antd";
import { Copyable } from "@vyne-shared/components/Copyable";
import { FormItemGroup } from "@vyne-shared/components/vyneForm/items/VyneFormItems";
import { StripeNoticeBadge } from "molecules/StripeNotice/StripeNotice";
import { getApiException } from "api/exceptions";
import { useAuth } from "auth/useAuth";
import { useStripeAccountStatus } from "api/clients/useGetStripeAccountStatus";
import type { AllConnectedAccountCallToAction } from "api/responses";

const { Title } = Typography;
const defaultErrorMessage = "Unable to get Stripe Account Data";

/** Page that displays credential settings that can be changed by the user. */
export const StripeConnectedAccountSettings = () => {
    const loginResult = useAuth((state) => state.loginResult);
    const { isPending, error, data } = useStripeAccountStatus();

    if (isPending) {
        return <Skeleton active={true} paragraph={{ rows: 2, width: [100, 100] }} />;
    }

    if (error) {
        const { detail } = getApiException(error, defaultErrorMessage);
        return <Result status="error" title={detail} subTitle="Please try again later." />;
    }

    const stripeAccountStatus = statusMap[data.status];
    const stripeConnectedAccountId = loginResult?.settings.stripeConnectedAccountId;

    return (
        <>
            <FormItemGroup legendTitle="Configure Payments" className="vyne-settings-tab-processor">
                <Space direction="vertical" size="small" className="stripe-credentials-data">
                    <Space direction="horizontal" size="middle">
                        <Title level={4} id="stripeIdLabel">
                            Stripe Connected Account ID
                        </Title>
                        <StripeNoticeBadge data={data} hideLink />
                    </Space>
                    <DisplayConnectedAccountId stripeConnectedAccountId={stripeConnectedAccountId} />
                </Space>
                <DisplayStripeSettingsButton stripeAccountStatus={stripeAccountStatus} />
            </FormItemGroup>
        </>
    );
};

type StripeAccountStatus = "Active" | "RequiredSettingsMissing" | "ContactSupport";

const statusMap: Record<AllConnectedAccountCallToAction, StripeAccountStatus> = {
    BeginEnrollment: "RequiredSettingsMissing",
    ContinueEnrollment: "RequiredSettingsMissing",
    FixEnrollment: "RequiredSettingsMissing",
    Wait: "RequiredSettingsMissing",
    WaitForProceed: "RequiredSettingsMissing",
    Proceed: "Active",
    ContactSupport: "ContactSupport",
};

/** Display Stripe Connected Account ID or warning if not set. */
const DisplayConnectedAccountId = ({ stripeConnectedAccountId }: { readonly stripeConnectedAccountId?: string }) =>
    stripeConnectedAccountId !== undefined ? (
        <Copyable className="stripe-copy-account-id" aria-labelledby="stripeIdLabel">
            {stripeConnectedAccountId}
        </Copyable>
    ) : (
        <Alert message="Stripe Connected Account ID has not been set yet." type="warning" showIcon />
    );

/** Display Stripe settings button or contact support information depending on account status. */
const DisplayStripeSettingsButton = ({ stripeAccountStatus }: { readonly stripeAccountStatus: StripeAccountStatus }) =>
    stripeAccountStatus === "Active" || stripeAccountStatus === "RequiredSettingsMissing" ? (
        <StripeSettingsButton />
    ) : (
        <ContactSupportInfo />
    );

// TODO: Re-enable once we disable ability to update bank account in Stripe
// type StripeSettingsButtonStatus = Extract<StripeAccountStatus, "Active" | "RequiredSettingsMissing">;
// const stripeButtonText: Record<StripeSettingsButtonStatus, string> = {
//     Active: "Update Stripe Account",
//     RequiredSettingsMissing: "Visit Stripe Settings",
// };

const StripeSettingsButton = () => {
    return (
        <p className="stripe-make-changes-text">
            Need to make changes to your Stripe account? Contact us at <a href="tel:+17705158905">770.515.8905</a> or
            email <a href="mailto:PaymentOperations@vynedental.com">PaymentOperations@vynedental.com</a>
        </p>
    );

    // TODO: Re-enable once we disable ability to update bank account in Stripe
    // {stripeAccountStatus,}: {readonly stripeAccountStatus: StripeSettingsButtonStatus;}
    // const { isPending, isError, data } = useStripeSettingsFormLink();
    // const queryClient = useQueryClient();
    // if (isPending) {
    //     return <Skeleton.Button active={true} size="large" />;
    // }
    // if (isError) {
    //     return <Alert message="Unable to get Stripe Settings Form Link" type="error" showIcon />;
    // }
    // return (
    //     <Button
    //         type="default"
    //         target="_blank"
    //         className="vyne-settings-tab-stripe-cta"
    //         href={data.url}
    //         onClick={() =>
    //             // invalidate query for stripe account status as stripe account status may have changed
    //             void queryClient.invalidateQueries({
    //                 queryKey: ["stripeAccountStatus"],
    //             })
    //         }
    //         icon={<ExportOutlined />}
    //     >
    //         {stripeButtonText[stripeAccountStatus]}
    //     </Button>
    // );
};

/** Display contact information for support if account status is "ContactSupport". */
const ContactSupportInfo = () => (
    <Space direction="vertical">
        <Title level={3}>Contact Us</Title>
        <div>
            <strong className="contact-label">Phone:</strong> <a href="tel:+1-866-712-9584">(866) 712-9584</a>
        </div>
        <div>
            <strong className="contact-label">Email:</strong>{" "}
            <a href="mailto:support@vynedental.com">support@vynedental.com</a>
        </div>
    </Space>
);
